/**
 * The admin session: who is signed in with Google, and how to connect or
 * sign out. Every admin screen reads it through useAdmin().
 */
import React, { createContext, useCallback, useContext, useState } from "react";
import { currentToken, requestToken, signOutGoogle } from "../../core/auth";

/**
 * @typedef {{
 *   google: { email?: string } | null,
 *   connectGoogle: () => Promise<{ ok: boolean, error?: string }>,
 *   logout: () => void,
 * }} AdminSession
 */

/** @type {React.Context<AdminSession | null>} */
const AdminContext = createContext(null);

// Signing in with Google is the whole gate. Access is enforced by Google, not
// by this check: the OAuth consent screen is Internal to the chloelavrat.com
// Workspace, and Cloud Storage only accepts writes from accounts with bucket
// permissions. This allowlist just turns "signed in as the wrong account"
// into a clear message instead of a wall of 403s.
/** The only Google account the admin accepts. */
export const ADMIN_EMAIL = "contact@chloelavrat.com";

/** Holds the Google session for /admin; only ADMIN_EMAIL is accepted. */
export function AdminProvider({ children }) {
	const [google, setGoogle] = useState(() => {
		const t = currentToken();
		return t && t.email === ADMIN_EMAIL ? t : null;
	});

	// Must be called from a click: Google's account chooser is a pop-up.
	const connectGoogle = useCallback(async () => {
		try {
			const t = await requestToken({ interactive: true });
			if (t.email && t.email !== ADMIN_EMAIL) {
				signOutGoogle();
				return {
					ok: false,
					error: `${t.email} cannot edit this site. Sign in as ${ADMIN_EMAIL}.`,
				};
			}
			setGoogle(t);
			return { ok: true };
		} catch (err) {
			return { ok: false, error: err.message };
		}
	}, []);

	const logout = useCallback(() => {
		setGoogle(null);
		signOutGoogle();
	}, []);

	return (
		<AdminContext.Provider value={{ google, connectGoogle, logout }}>
			{children}
		</AdminContext.Provider>
	);
}

/** The admin session. Throws outside AdminProvider. */
export function useAdmin() {
	const ctx = useContext(AdminContext);
	if (!ctx) throw new Error("useAdmin must be inside AdminProvider");
	return ctx;
}
